/**
 * Стани області (states.md): порожньо, помилка, немає доступу — іконка 24 px, заголовок,
 * одне речення, одна основна дія; помилка — з кодом для підтримки й кнопкою «Повідомити про
 * проблему» (UX-50, UX-51). Сторінка помилки (`page`) має заголовок `h1` і фокус на ньому: людина
 * з клавіатурою чи програмою читання чує, що сталося, і першою Tab-дією бачить «Спробувати ще раз».
 * Порожній стан з метаданих — EmptyState.tsx, очікування з порогом 300 мс — Pending.tsx.
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { formatTime } from '../i18n/format.ts';
import { common as t } from '../i18n/uk/common.ts';
import { supportCore as s } from '../i18n/uk/support-core.ts';
import { cleanCode, supportCode } from '../support/classify.ts';
import { noteError } from '../support/diagnostics.ts';
import { openReport } from '../support/report.ts';
import { Icon, type IconName } from './Icon.tsx';

interface StateViewProps {
  icon: IconName;
  title: string;
  text?: string | undefined;
  tone?: 'error';
  code?: string | undefined;
  /** `traceId` відповіді: іде у звернення, на екрані його немає. */
  traceId?: string | null | undefined;
  /** Сторінка помилки замість області: `h1` і фокус на заголовку. */
  page?: boolean;
  /** Кнопка «Повідомити про проблему»: типово там, де є код для підтримки. */
  report?: boolean;
  /** Своя назва кнопки звернення (403: «Попросити доступ»). */
  reportLabel?: string;
  children?: ReactNode;
}

export function StateView({
  icon,
  title,
  text,
  tone,
  code,
  traceId,
  page,
  report,
  reportLabel,
  children,
}: StateViewProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  // Час помилки — коли її побачила людина, а не час останнього рендеру.
  const [at] = useState(() => new Date().toISOString());
  const showReport = Boolean(code) && report !== false;

  useEffect(() => {
    if (page) heading.current?.focus();
  }, [page]);
  // Журнал останніх помилок для звернення: лише код і traceId (support/diagnostics.ts).
  useEffect(() => {
    if (code) noteError(code, traceId ?? null);
  }, [code, traceId]);

  return (
    <div
      className={tone === 'error' ? 'state state--error' : 'state'}
      role={tone ? 'alert' : undefined}
    >
      <Icon name={icon} />
      {page ? (
        <h1 className="state__title" ref={heading} tabIndex={-1}>
          {title}
        </h1>
      ) : (
        <p className="state__title">{title}</p>
      )}
      {text && <p className="state__text">{text}</p>}
      {(children || showReport) && (
        <div className="state__actions">
          {children}
          {showReport && (
            <button
              className="btn btn--ghost"
              type="button"
              onClick={() => openReport({ code: cleanCode(code), traceId: traceId ?? null })}
            >
              {reportLabel ?? s.reportAction}
            </button>
          )}
        </div>
      )}
      {code && (
        <p className="state__code">
          {t.supportCode}: <span className="state__code-value">{code}</span> · {formatTime(at)}
        </p>
      )}
    </div>
  );
}

/** Код для підтримки з будь-якої помилки (`ApiError`, `ApiProblem`, невідома): див. support/classify.ts. */
export const errorCode = supportCode;
